import React, { useState, useMemo } from "react";
import { StyleSheet, View, ScrollView, TouchableOpacity, Image } from "react-native";
import { router } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { MemoryModel, MediaModel, formatLocation } from "common247";
import ThemedScreen from "@/components/ThemedScreen";
import { ThemedText } from "@/components/ThemedText";
import { ThemedPanel } from "@/components/ThemedPanel";
import { ThemedButton } from "@/components/ThemedButton";
import { MemorySelector } from "@/components/memory/MemorySelector";
import { ComponentRefTag } from "@/components/design/ComponentRefTag";
import { useThemeColor } from "@/hooks/useThemeColor";
import { BrandColors, Spacing } from "@/constants/ThemeColours";

/**
 * Rich mock memories for testing the MemorySelector in various conditions:
 * small sets, long lists requiring scrolling, search filtering, and varied metadata.
 */
const MOCK_MEMORIES_BASE: MemoryModel[] = [
  {
    id: "demo-mem-1",
    title: "Pacific Coast Highway Roadtrip",
    description: "Scenic archival drive down Highway 1 through Big Sur and Monterey cliffs.",
    date: "1974-08-14",
    location: { text: "Big Sur, California" },
    ownerId: "demo-user",
    ownerDisplayName: "Evelyn Vance",
    userRole: "owner",
    canShare: true,
    mediaIds: ["demo-media-1", "demo-media-2"],
    createdAt: Date.parse("1974-08-14T10:00:00.000Z"),
  },
  {
    id: "demo-mem-2",
    title: "Summer Solstice Garden Gathering",
    description: "Annual heritage gathering in the botanical gardens with heirloom tea service.",
    date: "1982-06-21",
    location: { text: "Carmel Valley, California" },
    ownerId: "demo-user",
    ownerDisplayName: "Evelyn Vance",
    userRole: "owner",
    canShare: true,
    mediaIds: ["demo-media-2"],
    createdAt: Date.parse("1982-06-21T18:30:00.000Z"),
  },
  {
    id: "demo-mem-3",
    title: "Monterey Maritime Archives Field Survey",
    description: "Oral history capture of lighthouse keepers and historic sea chart drafts.",
    date: "1991-10-04",
    location: { text: "Point Pinos, California" },
    ownerId: "demo-user",
    ownerDisplayName: "Silas Thorne",
    userRole: "owner",
    canShare: true,
    mediaIds: [],
    createdAt: Date.parse("1991-10-04T14:00:00.000Z"),
  },
  {
    id: "demo-mem-4",
    title: "Old Town Architectural Restoration",
    description: "Detailed photo-documentation of Victorian facades and historic brickwork.",
    date: "1995-04-18",
    location: { text: "Eureka, California" },
    ownerId: "demo-user",
    userRole: "owner",
    canShare: true,
    mediaIds: ["demo-media-3", "demo-media-4"],
    createdAt: Date.parse("1995-04-18T11:00:00.000Z"),
  },
  {
    id: "demo-mem-5",
    title: "Redwood Canopy Botany Expedition",
    description: "Field notebooks and macro-specimens of rare coastal fern varieties.",
    date: "1998-09-02",
    location: { text: "Muir Woods, California" },
    ownerId: "demo-user",
    userRole: "owner",
    canShare: true,
    mediaIds: [],
    createdAt: Date.parse("1998-09-02T08:30:00.000Z"),
  },
  {
    id: "demo-mem-6",
    title: "Sierra Nevada Autumn Geological Trek",
    description: "Granite ridge formations, high-altitude glacial lakes, and field sketchbooks.",
    date: "2003-10-12",
    location: { text: "Yosemite Valley, California" },
    ownerId: "demo-user",
    userRole: "owner",
    canShare: true,
    mediaIds: ["demo-media-1"],
    createdAt: Date.parse("2003-10-12T15:20:00.000Z"),
  },
  {
    id: "demo-mem-7",
    title: "Lighthouse Keeper's Logbook Rediscovery",
    description: "Transcription of 19th century weather anomalies recorded at Cape Mendocino.",
    date: "2007-02-19",
    location: { text: "Cape Mendocino, California" },
    ownerId: "demo-user",
    userRole: "owner",
    canShare: true,
    mediaIds: [],
    createdAt: Date.parse("2007-02-19T09:40:00.000Z"),
  },
  {
    id: "demo-mem-8",
    title: "San Francisco Bay Maritime Regatta",
    description: "Traditional wooden sailboat race captured on Kodachrome film stock.",
    date: "2010-07-28",
    location: { text: "San Francisco Bay, California" },
    ownerId: "demo-user",
    userRole: "owner",
    canShare: true,
    mediaIds: ["demo-media-2", "demo-media-3"],
    createdAt: Date.parse("2010-07-28T16:00:00.000Z"),
  },
  {
    id: "demo-mem-9",
    title: "Napa Valley Harvest & Cellar Inspection",
    description: "Century-old barrel aging caves, vineyard stone masonry, and vintage press catalog.",
    date: "2014-09-24",
    location: { text: "St. Helena, California" },
    ownerId: "demo-user",
    userRole: "owner",
    canShare: true,
    mediaIds: [],
    createdAt: Date.parse("2014-09-24T13:10:00.000Z"),
  },
  {
    id: "demo-mem-10",
    title: "Mendocino Headlands Coastal Wildflowers",
    description: "Spring botanical bloom atop sea bluffs with crashing Pacific breakers below.",
    date: "2018-05-11",
    location: { text: "Mendocino, California" },
    ownerId: "demo-user",
    userRole: "owner",
    canShare: true,
    mediaIds: ["demo-media-4"],
    createdAt: Date.parse("2018-05-11T11:45:00.000Z"),
  },
  {
    id: "demo-mem-11",
    title: "Point Reyes Estuary Bird Sanctuary",
    description: "Migratory coastal avian observations with telescopic lens captures.",
    date: "2021-11-06",
    location: { text: "Point Reyes, California" },
    ownerId: "demo-user",
    userRole: "owner",
    canShare: true,
    mediaIds: [],
    createdAt: Date.parse("2021-11-06T07:15:00.000Z"),
  },
  {
    id: "demo-mem-12",
    title: "Historic Cable Car Workshop Tour",
    description: "Subterranean winding wheels and Victorian transit heritage machinery.",
    date: "2024-03-30",
    location: { text: "San Francisco, California" },
    ownerId: "demo-user",
    userRole: "owner",
    canShare: true,
    mediaIds: ["demo-media-1", "demo-media-2"],
    createdAt: Date.parse("2024-03-30T14:50:00.000Z"),
  },
];

/**
 * Demo media items selected to append to a memory.
 */
const ALL_DEMO_MEDIA: MediaModel[] = [
  {
    id: "demo-media-1",
    title: "Big Sur Coastal Fog & Breakers",
    url: "https://images.unsplash.com/photo-1506744038136-46273834b3fb",
    mediaCategory: "image",
    ownerId: "demo-user",
    createdAt: Date.now() - 3600000,
  },
  {
    id: "demo-media-2",
    title: "Point Pinos Lighthouse Sunset",
    url: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e",
    mediaCategory: "image",
    ownerId: "demo-user",
    createdAt: Date.now() - 7200000,
  },
  {
    id: "demo-media-3",
    title: "Redwood Canopy Sunlight Rays",
    url: "https://images.unsplash.com/photo-1448375240586-882707db888b",
    mediaCategory: "image",
    ownerId: "demo-user",
    createdAt: Date.now() - 10800000,
  },
  {
    id: "demo-media-4",
    title: "Maritime Harbor Chart Draft",
    url: "https://images.unsplash.com/photo-1524995997946-a1c2e315a42f",
    mediaCategory: "image",
    ownerId: "demo-user",
    createdAt: Date.now() - 14400000,
  },
];

type MemoryListSize = "small" | "large" | "empty";
type MediaSelectionCount = 1 | 2 | 4;

/**
 * MemorySelector (Add to Memory) Designer Showcase Screen.
 * Allows interactive evaluation of MemorySelector across various memory count configurations,
 * scroll behavior testing (scrollbars), search filtering, and creation triggers.
 */
export default function AddToMemoryDesignerScreen() {
  const [modalVisible, setModalVisible] = useState(false);
  const [listSize, setListSize] = useState<MemoryListSize>("large");
  const [mediaCount, setMediaCount] = useState<MediaSelectionCount>(2);
  const [lastActionResult, setLastActionResult] = useState<string | null>(null);
  const [selectedTargetMemory, setSelectedTargetMemory] = useState<MemoryModel | null>(null);

  const cardBg = useThemeColor({}, "card");
  const screenBg = useThemeColor({}, "background");
  const borderColor = useThemeColor({}, "border");
  const tintColor = useThemeColor({}, "tint");
  const textMuted = useThemeColor({}, "textMuted");
  const badgeBg = useThemeColor({}, "buttonSecondary");

  const activeMemories = useMemo(() => {
    if (listSize === "empty") return [];
    if (listSize === "small") return MOCK_MEMORIES_BASE.slice(0, 3);
    return MOCK_MEMORIES_BASE;
  }, [listSize]);

  const activeSelectedMedia = useMemo(() => {
    return ALL_DEMO_MEDIA.slice(0, mediaCount);
  }, [mediaCount]);

  const handleSuccess = (target: MemoryModel) => {
    setSelectedTargetMemory(target);
    setLastActionResult(`Successfully added ${mediaCount} media item(s) to "${target.title}"`);
    setModalVisible(false);
  };

  const handleCreateNewMemory = () => {
    setLastActionResult("Triggered '+ Create New Memory' redirect action");
    setModalVisible(false);
  };

  return (
    <ThemedScreen
      title="AddToMemory / MemorySelector Designer"
      leftIcon={{
        name: "arrow-back",
        onPress: () => router.navigate("/admin/design" as any),
      }}
    >
      <ScrollView contentContainerStyle={styles.container}>
        {/* ========================================================================= */}
        {/* Overview Header */}
        {/* ========================================================================= */}
        <ThemedPanel padding={14} elevation="low" style={styles.sectionPanel}>
          <View style={styles.sectionHeader}>
            <View style={styles.sectionTitleGroup}>
              <View style={[styles.iconCircle, { backgroundColor: badgeBg }]}>
                <Ionicons name="folder-open" size={22} color={tintColor} />
              </View>
              <View style={{ flex: 1 }}>
                <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
                  MemorySelector (Add to Memory Mode) Dialog
                </ThemedText>
                <ThemedText style={[styles.sectionSubtitle, { color: textMuted }]}>
                  Bulk dialog allowing users to assign selected media items to an existing Memory with live search filtering, persistent scrollbars, and new memory creation workflows.
                </ThemedText>
              </View>
            </View>
            <ComponentRefTag name="REF: <MemorySelector>" />
          </View>
        </ThemedPanel>

        {/* ========================================================================= */}
        {/* Interactive Configuration Workbench */}
        {/* ========================================================================= */}
        <ThemedPanel padding={14} elevation="low" style={styles.sectionPanel}>
          <ThemedText type="defaultSemiBold" style={styles.cardHeaderTitle}>
            Harness Configuration
          </ThemedText>

          {/* Memory List Size Picker */}
          <ThemedText style={[styles.configLabel, { color: textMuted }]}>
            Available Memories in Modal:
          </ThemedText>
          <View style={styles.toggleRow}>
            <TouchableOpacity
              style={[
                styles.toggleOption,
                { borderColor },
                listSize === "small" && { backgroundColor: tintColor, borderColor: tintColor },
              ]}
              onPress={() => setListSize("small")}
            >
              <ThemedText
                style={[
                  styles.toggleOptionText,
                  listSize === "small" && { color: "#FFFFFF", fontWeight: "700" },
                ]}
              >
                Small (3 Items)
              </ThemedText>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.toggleOption,
                { borderColor },
                listSize === "large" && { backgroundColor: tintColor, borderColor: tintColor },
              ]}
              onPress={() => setListSize("large")}
            >
              <ThemedText
                style={[
                  styles.toggleOptionText,
                  listSize === "large" && { color: "#FFFFFF", fontWeight: "700" },
                ]}
              >
                Large (12 Items, Scrollbar)
              </ThemedText>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.toggleOption,
                { borderColor },
                listSize === "empty" && { backgroundColor: tintColor, borderColor: tintColor },
              ]}
              onPress={() => setListSize("empty")}
            >
              <ThemedText
                style={[
                  styles.toggleOptionText,
                  listSize === "empty" && { color: "#FFFFFF", fontWeight: "700" },
                ]}
              >
                Empty (0 Items)
              </ThemedText>
            </TouchableOpacity>
          </View>

          {/* Selected Media Count Picker */}
          <ThemedText style={[styles.configLabel, { color: textMuted, marginTop: 14 }]}>
            Media Items to Append:
          </ThemedText>
          <View style={styles.toggleRow}>
            {[1, 2, 4].map((count) => (
              <TouchableOpacity
                key={count}
                style={[
                  styles.toggleOption,
                  { borderColor },
                  mediaCount === count && { backgroundColor: tintColor, borderColor: tintColor },
                ]}
                onPress={() => setMediaCount(count as MediaSelectionCount)}
              >
                <ThemedText
                  style={[
                    styles.toggleOptionText,
                    mediaCount === count && { color: "#FFFFFF", fontWeight: "700" },
                  ]}
                >
                  {count} Media Item{count === 1 ? "" : "s"}
                </ThemedText>
              </TouchableOpacity>
            ))}
          </View>

          {/* Selected Media Preview Thumbnails */}
          <View style={styles.mediaPreviewContainer}>
            <ThemedText style={[styles.configLabel, { color: textMuted }]}>
              Selected Media Preview:
            </ThemedText>
            <View style={styles.thumbnailRow}>
              {activeSelectedMedia.map((m) => (
                <View key={m.id} style={[styles.thumbnailCard, { borderColor, backgroundColor: screenBg }]}>
                  <Image source={{ uri: m.url }} style={styles.thumbnailImage} />
                  <ThemedText style={styles.thumbnailTitle} numberOfLines={1}>
                    {m.title}
                  </ThemedText>
                </View>
              ))}
            </View>
          </View>

          {/* Launch Trigger Button */}
          <View style={styles.launchButtonContainer}>
            <ThemedButton
              title={`Launch MemorySelector (${activeSelectedMedia.length} Media)`}
              icon="folder-open-outline"
              variant="primary"
              onPress={() => setModalVisible(true)}
            />
          </View>
        </ThemedPanel>

        {/* ========================================================================= */}
        {/* Live Inspector Panel */}
        {/* ========================================================================= */}
        <ThemedPanel padding={14} elevation="low" style={styles.sectionPanel}>
          <ThemedText type="defaultSemiBold" style={styles.cardHeaderTitle}>
            Live Inspector
          </ThemedText>

          <View style={[styles.inspectorBox, { backgroundColor: cardBg, borderColor }]}>
            <ThemedText style={styles.inspectorLabel}>Last Action Result:</ThemedText>
            {lastActionResult ? (
              <ThemedText style={styles.inspectorValue}>{lastActionResult}</ThemedText>
            ) : (
              <ThemedText style={styles.inspectorPlaceholder}>
                No memory selected yet. Click "Launch MemorySelector" above to test.
              </ThemedText>
            )}
          </View>

          {selectedTargetMemory && (
            <View style={[styles.inspectorBox, { backgroundColor: cardBg, borderColor, marginTop: 10 }]}>
              <ThemedText style={styles.inspectorLabel}>Selected Memory Details:</ThemedText>
              <View style={styles.detailRow}>
                <ThemedText style={styles.detailLabel}>Title:</ThemedText>
                <ThemedText style={styles.detailValue}>{selectedTargetMemory.title}</ThemedText>
              </View>
              <View style={styles.detailRow}>
                <ThemedText style={styles.detailLabel}>Memory ID:</ThemedText>
                <ThemedText style={styles.detailValue}>{selectedTargetMemory.id}</ThemedText>
              </View>
              <View style={styles.detailRow}>
                <ThemedText style={styles.detailLabel}>Date:</ThemedText>
                <ThemedText style={styles.detailValue}>{selectedTargetMemory.date || "N/A"}</ThemedText>
              </View>
              {selectedTargetMemory.location && (
                <View style={styles.detailRow}>
                  <ThemedText style={styles.detailLabel}>Location:</ThemedText>
                  <ThemedText style={styles.detailValue}>
                    {formatLocation(selectedTargetMemory.location)}
                  </ThemedText>
                </View>
              )}
              <View style={styles.detailRow}>
                <ThemedText style={styles.detailLabel}>Existing Media Count:</ThemedText>
                <ThemedText style={styles.detailValue}>
                  {selectedTargetMemory.mediaIds?.length ?? 0}
                </ThemedText>
              </View>
            </View>
          )}
        </ThemedPanel>

        {/* ========================================================================= */}
        {/* Rendered Modal Under Test */}
        {/* ========================================================================= */}
        <MemorySelector
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
          selectedCount={activeSelectedMedia.length}
          itemTypeLabel="media"
          memories={activeMemories}
          onSelectMemory={(memory) => {
            handleSuccess(memory);
            setModalVisible(false);
          }}
          onCreateNewMemory={handleCreateNewMemory}
        />
      </ScrollView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 12,
    paddingBottom: 40,
  },
  sectionPanel: {
    marginBottom: 14,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  sectionTitleGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    flex: 1,
    marginRight: 8,
  },
  iconCircle: {
    width: 44,
    height: 44,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  sectionTitle: {
    fontSize: 16,
  },
  sectionSubtitle: {
    fontSize: 12,
    marginTop: 4,
    lineHeight: 16,
  },
  cardHeaderTitle: {
    fontSize: 14,
    marginBottom: 10,
  },
  configLabel: {
    fontSize: 12,
    fontWeight: "600",
    marginBottom: 6,
  },
  toggleRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  toggleOption: {
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 6,
    borderWidth: 1,
  },
  toggleOptionText: {
    fontSize: 12,
    fontWeight: "500",
  },
  mediaPreviewContainer: {
    marginTop: 14,
  },
  thumbnailRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 4,
  },
  thumbnailCard: {
    width: 100,
    borderRadius: 6,
    borderWidth: 1,
    overflow: "hidden",
  },
  thumbnailImage: {
    width: "100%",
    height: 60,
    backgroundColor: "rgba(0,0,0,0.05)",
  },
  thumbnailTitle: {
    fontSize: 10,
    padding: 4,
  },
  launchButtonContainer: {
    marginTop: 16,
    alignItems: "flex-start",
  },
  inspectorBox: {
    borderWidth: 1,
    borderRadius: 6,
    padding: 10,
  },
  inspectorLabel: {
    fontSize: 11,
    fontWeight: "700",
    opacity: 0.6,
    marginBottom: 4,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  inspectorValue: {
    fontSize: 13,
    fontWeight: "600",
  },
  inspectorPlaceholder: {
    fontSize: 12,
    fontStyle: "italic",
    opacity: 0.5,
  },
  detailRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 3,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: "rgba(0,0,0,0.06)",
  },
  detailLabel: {
    fontSize: 12,
    opacity: 0.7,
  },
  detailValue: {
    fontSize: 12,
    fontWeight: "600",
  },
});
